import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';
import SubscriptionModal from '../components/Subscription/SubscriptionModal';

interface SubscriptionPlan {
  id: string;
  name: string;
  duration_months: number;
  price: number;
  currency: string;
  features: string[];
  popular?: boolean;
  recommended?: boolean;
}

interface SubscriptionStatus {
  subscription_status: string;
  has_access: boolean;
  trial_start_date?: string;
  trial_end_date?: string;
  days_remaining?: number;
  total_articles_read: number;
  daily_article_count: number;
  message: string;
}

interface SubscriptionContextType {
  subscriptionStatus: SubscriptionStatus | null;
  plans: SubscriptionPlan[];
  loading: boolean;
  showSubscriptionModal: boolean;
  setShowSubscriptionModal: (show: boolean) => void;
  checkSubscription: () => Promise<void>;
  handleSubscriptionRequired: (message?: string) => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [modalMessage, setModalMessage] = useState<string | undefined>();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      checkSubscription();
      fetchPlans();
    } else {
      setLoading(false);
    }
  }, []);

  const checkSubscription = async () => {
    try {
      const response = await api.get('/subscription/status');
      setSubscriptionStatus(response.data);

      // If subscription expired or trial expired, show modal
      if (response.data.subscription_status === 'TRIAL_EXPIRED' ||
          response.data.subscription_status === 'SUBSCRIPTION_EXPIRED') {
        setModalMessage(response.data.message);
        setShowSubscriptionModal(true);
      }
    } catch (error) {
      console.error('Failed to check subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await api.get('/subscription/plans');
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const handleSubscriptionRequired = useCallback((message?: string) => {
    setModalMessage(message || subscriptionStatus?.message);
    setShowSubscriptionModal(true);
  }, [subscriptionStatus?.message]);

  // Set up axios interceptor to handle subscription errors
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 403) {
          const headers = error.response.headers;
          if (headers['x-subscription-required'] === 'true') {
            handleSubscriptionRequired(error.response.data.detail);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [handleSubscriptionRequired]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscriptionStatus,
        plans,
        loading,
        showSubscriptionModal,
        setShowSubscriptionModal,
        checkSubscription,
        handleSubscriptionRequired,
      }}
    >
      {children}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        plans={plans}
        message={modalMessage}
        trialDaysRemaining={subscriptionStatus?.days_remaining}
      />
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;